import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Bot, ChevronRight, Plus, Trash2 } from 'lucide-react';
import type { Role } from '@reflex/shared';
import {
  getListServiceAccountsQueryKey,
  useCreateServiceAccount,
  useDeleteServiceAccount,
  useListServiceAccounts,
  type ListServiceAccountsQueryResult,
} from '@reflex/ui/client/generated/service-accounts';
import { formatDateOnly } from '@reflex/ui/lib/format';
import { SectionCard } from '@reflex/ui/components/shared/SectionCard';
import { EmptyState } from '@reflex/ui/components/shared/EmptyState';
import { Button } from '@reflex/ui/components/ui/button';
import { Input } from '@reflex/ui/components/ui/input';
import { Label } from '@reflex/ui/components/ui/label';
import { Badge } from '@reflex/ui/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@reflex/ui/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@reflex/ui/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@reflex/ui/components/ui/alert-dialog';
import { PageLayout, PageHeader } from '@/components/layout/PageLayout';
import { HubReadGuard } from '@reflex/ui/components/layout/NoReadAccessState';
import { AsyncContent } from '@/components/shared/AsyncContent';
import { RequireActiveOrganization } from '@/components/shared/RequireActiveOrganization';
import { useAuth } from '@/hooks/auth/useAuth';
import { api } from '@/lib/api';
import { useOrgRoutes } from '@/lib/useOrgRoutes';
import { ServiceAccountDetailPage } from './ServiceAccountDetailPage';

type ServiceAccount = ListServiceAccountsQueryResult['serviceAccounts'][number];

/**
 * Service accounts list (and in-place detail when `:id` is in the URL).
 * When `embedded`, the outer page chrome is omitted for the Organization hub.
 */
export function ServiceAccountsPage({ embedded = false }: { embedded?: boolean }) {
  const { id } = useParams<{ id?: string }>();
  return (
    <HubReadGuard permission="service-accounts:read">
      {id ? (
        <ServiceAccountDetailPage embedded={embedded} />
      ) : (
        <RequireActiveOrganization title="Service accounts">
          {(org) => <ServiceAccountsBody key={org.id} orgId={org.id} embedded={embedded} />}
        </RequireActiveOrganization>
      )}
    </HubReadGuard>
  );
}

function ServiceAccountsBody({ orgId, embedded = false }: { orgId: string; embedded?: boolean }) {
  const routes = useOrgRoutes();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const canWrite = can('service-accounts:write');
  // Creating an account assigns it a role, so it also needs `roles:manage`
  // (mirrors the server-side guard on `POST /service-accounts`).
  const canCreate = canWrite && can('roles:manage');

  const listQueryKey = getListServiceAccountsQueryKey();
  const accountsQuery = useListServiceAccounts();
  const accounts = accountsQuery.data?.serviceAccounts ?? [];

  const rolesQuery = useQuery({
    queryKey: ['organizations', orgId, 'roles'],
    queryFn: () => api.listOrganizationRoles(orgId),
    enabled: !!orgId && canCreate,
  });
  // The API's role list already hides the platform-admin role; keep only
  // org-scoped roles for the picker (team roles are not assignable here).
  const assignableRoles = (rolesQuery.data ?? []).filter((role) => role.scope === 'org');

  const [createOpen, setCreateOpen] = useState(false);
  const [deleting, setDeleting] = useState<ServiceAccount | null>(null);

  const createMutation = useCreateServiceAccount({
    mutation: {
      onSuccess: (result) => {
        void queryClient.invalidateQueries({ queryKey: listQueryKey });
        toast.success('Service account created');
        setCreateOpen(false);
        // Hand off to the detail page, where keys are minted.
        navigate(routes.serviceAccount(result.serviceAccount.id));
      },
      onError: (err) =>
        toast.error('Failed to create service account', {
          description: err instanceof Error ? err.message : undefined,
        }),
    },
  });

  const deleteMutation = useDeleteServiceAccount({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: listQueryKey });
        toast.success('Service account deleted');
      },
      onError: (err) =>
        toast.error('Failed to delete service account', {
          description: err instanceof Error ? err.message : undefined,
        }),
    },
  });

  const isPending = createMutation.isPending || deleteMutation.isPending;

  // The Organization hub already renders the "Organization" title and the
  // General / Service accounts section rail, so omit the section title when
  // embedded — keep the description and the Create action.
  const header = (
    <PageHeader
      {...(embedded
        ? {}
        : { iconVariant: 'box' as const, icon: <Bot className="h-5 w-5 text-muted-foreground" /> })}
      title={embedded ? undefined : 'Service accounts'}
      description="Machine identities for this organization. Each account holds a role and can have its own API keys for CI, scripts, and automation."
      actions={
        canCreate ? (
          <Button
            size="sm"
            onClick={() => setCreateOpen(true)}
            disabled={isPending}
            aria-label="Create service account"
          >
            <Plus className="icon-sm" />
            <span className="sm:hidden">Create</span>
            <span className="hidden sm:inline">Create service account</span>
          </Button>
        ) : undefined
      }
    />
  );

  const body = (
    <>
      <SectionCard
        title="All service accounts"
        description="A service account calls the Reflex API with the permissions of its role. Open one to mint or revoke its API keys."
        data-testid="service-accounts-section"
      >
        <AsyncContent
          isLoading={accountsQuery.isLoading}
          isError={accountsQuery.isError}
          loadingMessage="Loading service accounts…"
          errorMessage="Failed to load service accounts."
        >
          {accounts.length > 0 ? (
            <ul className="divide-y divide-border rounded-md border">
              {accounts.map((account) => (
                <li
                  key={account.id}
                  className="flex items-center gap-3 pr-3"
                  data-testid={`service-account-row-${account.id}`}
                >
                  <Link
                    to={routes.serviceAccount(account.id)}
                    aria-label={`View service account ${account.name}`}
                    className="flex min-w-0 flex-1 items-center justify-between gap-3 p-4 transition-colors hover:bg-muted/40"
                  >
                    <div className="min-w-0 flex-1 space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{account.name}</span>
                        {account.roles.map((role) => (
                          <Badge key={role.id} variant="secondary">
                            {role.name}
                          </Badge>
                        ))}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Created {formatDateOnly(account.createdAt)}
                      </p>
                    </div>
                    <ChevronRight className="icon-sm shrink-0 text-muted-foreground" />
                  </Link>
                  {canWrite && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="shrink-0"
                      onClick={() => setDeleting(account)}
                      disabled={isPending}
                      aria-label={`Delete ${account.name}`}
                    >
                      <Trash2 className="icon-sm" /> Delete
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState
              variant="bordered"
              title="No service accounts yet. Create one to give a script, CI job, or integration its own identity and API keys."
            />
          )}
        </AsyncContent>
      </SectionCard>

      <CreateServiceAccountDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        roles={assignableRoles}
        rolesLoading={rolesQuery.isLoading}
        isPending={createMutation.isPending}
        onSubmit={(name, roleId) => createMutation.mutate({ data: { name, roleId } })}
      />

      <AlertDialog
        open={deleting !== null}
        onOpenChange={(next) => {
          if (!next) setDeleting(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this service account?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleting?.name} and all of its API keys will be permanently deleted. Any automation
              using those keys loses access immediately.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => {
                if (deleting) deleteMutation.mutate({ id: deleting.id });
                setDeleting(null);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );

  if (embedded) {
    return (
      <div className="space-y-5">
        {header}
        {body}
      </div>
    );
  }

  return (
    <PageLayout maxWidth="4xl">
      {header}
      {body}
    </PageLayout>
  );
}

interface CreateServiceAccountDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  roles: Role[];
  rolesLoading: boolean;
  isPending: boolean;
  onSubmit: (name: string, roleId: string) => void;
}

/** Name the account and pick the org role it should act with. */
function CreateServiceAccountDialog({
  open,
  onOpenChange,
  roles,
  rolesLoading,
  isPending,
  onSubmit,
}: CreateServiceAccountDialogProps) {
  const [name, setName] = useState('');
  const [roleId, setRoleId] = useState('');

  function handleOpenChange(next: boolean) {
    if (!next) {
      setName('');
      setRoleId('');
    }
    onOpenChange(next);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed || !roleId || isPending) return;
    onSubmit(trimmed, roleId);
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Create service account</DialogTitle>
            <DialogDescription>
              Name the account after where it runs, then pick the role whose permissions it should
              have in this organization.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="service-account-name">Name</Label>
              <Input
                id="service-account-name"
                placeholder="e.g. ci-deploy-bot"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoComplete="off"
                maxLength={100}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="service-account-role">Role</Label>
              <Select value={roleId} onValueChange={setRoleId}>
                <SelectTrigger id="service-account-role" aria-label="Role">
                  <SelectValue placeholder={rolesLoading ? 'Loading roles…' : 'Select a role'} />
                </SelectTrigger>
                <SelectContent>
                  {roles.map((role) => (
                    <SelectItem key={role.id} value={role.id}>
                      {role.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="mt-6">
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!name.trim() || !roleId || isPending}>
              {isPending ? 'Creating…' : 'Create account'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
